/**
 * The bridge between game logic and the renderer.
 *
 * A plain mutable object, never React state. Two producers write into it — the
 * offline tutorial simulation and the network sync adapter — and the R3F scene
 * reads it inside `useFrame`. That is what keeps the render loop free of React
 * work while still driving everything from one description of the world.
 */
import { stanceHome } from '@spell/combat-rules';
import { COMBAT } from '@spell/shared-types';

export interface ProjectileView {
  id: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  radius: number;
  color: string;
  accentColor: string;
  shape: string;
  element: string;
  homing: number;
  /** True when this one is coming at the local player. */
  incoming: boolean;
  parryable: boolean;
  /** Seconds until predicted impact; drives the warning ring. */
  eta: number;
  /**
   * Seconds left in the impact hold, or 0 when the projectile is still in
   * flight. Non-zero means it has arrived and is waiting out the last-chance
   * parry beat — expressed as a duration rather than a timestamp so the
   * tutorial's offline simulation and the networked room can both produce it
   * without agreeing on a clock.
   */
  resolveIn: number;
  armUntil: number;
  complexity: number;
}

export interface ImpactView {
  id: number;
  x: number;
  y: number;
  z: number;
  color: string;
  kind: 'damage' | 'heal' | 'shield' | 'reflect' | 'absorb' | 'dot';
  amount: number;
  bornAt: number;
  /** Seconds the flash lasts. */
  life: number;
  /**
   * True when the local player took this hit. A world-space shockwave centred
   * on the camera would swallow the screen, so self-hits are drawn small and
   * pushed out in front; the full-screen reaction is the HUD flash and the
   * camera kick.
   */
  self?: boolean;
}

export interface CircleView {
  active: boolean;
  /** 0..1 build-up progress. */
  progress: number;
  color: string;
  accent: string;
  complexity: number;
  /** Set on a successful parry to trigger the burst. */
  burstAt: number;
}

export interface SceneBus {
  /** `match` and `practice` are both driven by a room; `tutorial` is offline. */
  mode: 'idle' | 'tutorial' | 'match' | 'practice';
  /** Which end of the arena the local player was seated at. */
  selfSide: number;
  yaw: number;
  pitch: number;

  /**
   * Where the local player is standing, predicted locally each frame so the
   * camera answers the key instantly rather than a round trip later.
   */
  selfX: number;
  selfZ: number;
  /**
   * The server's last word on that position. The input loop eases the
   * prediction onto it; the server, not this pair, decides where anyone is.
   */
  serverSelfX: number;
  serverSelfZ: number;
  /** Conditions the prediction has to honour, or it will fight the server. */
  selfStunned: boolean;
  selfSlowed: boolean;

  selfHp: number;
  selfMaxHp: number;
  selfMana: number;
  selfMaxMana: number;
  selfShield: number;

  oppHp: number;
  oppMaxHp: number;
  oppMana: number;
  oppMaxMana: number;
  oppSide: number;
  /** Opponent's floor position, eased between server ticks. */
  oppX: number;
  oppZ: number;
  oppCasting: boolean;
  oppCastProgress: number;
  oppCastColor: string;
  oppStunned: boolean;
  oppPresent: boolean;

  projectiles: ProjectileView[];
  impacts: ImpactView[];

  circle: CircleView;

  /** 0..1 danger reading for the incoming-attack warning ring. */
  danger: number;
  /** Remaining hit-stop in seconds; scales the whole simulation while > 0. */
  hitStop: number;
  /** Time scale for slow motion (1 = normal). */
  timeScale: number;
  /** Decaying camera shake amplitude. */
  shake: number;
  /** Set when the local player is casting; drives wand pose + trail. */
  casting: boolean;
  castColor: string;

  /** Measured frames per second, sampled by the render loop. */
  fps: number;
}

export const scene: SceneBus = {
  mode: 'idle',
  selfSide: 1,
  yaw: 0,
  pitch: 0,

  selfX: stanceHome(1).x,
  selfZ: stanceHome(1).z,
  serverSelfX: stanceHome(1).x,
  serverSelfZ: stanceHome(1).z,
  selfStunned: false,
  selfSlowed: false,

  selfHp: COMBAT.MAX_HP,
  selfMaxHp: COMBAT.MAX_HP,
  selfMana: COMBAT.MAX_MANA,
  selfMaxMana: COMBAT.MAX_MANA,
  selfShield: 0,

  oppHp: COMBAT.MAX_HP,
  oppMaxHp: COMBAT.MAX_HP,
  oppMana: COMBAT.MAX_MANA,
  oppMaxMana: COMBAT.MAX_MANA,
  oppSide: -1,
  oppX: stanceHome(-1).x,
  oppZ: stanceHome(-1).z,
  oppCasting: false,
  oppCastProgress: 0,
  oppCastColor: '#ff5a3c',
  oppStunned: false,
  oppPresent: false,

  projectiles: [],
  impacts: [],

  circle: { active: false, progress: 0, color: '#8fd8ff', accent: '#ffffff', complexity: 2, burstAt: 0 },

  danger: 0,
  hitStop: 0,
  timeScale: 1,
  shake: 0,
  casting: false,
  castColor: '#8fd8ff',

  fps: 60,
};

let impactSeq = 0;

export function pushImpact(view: Omit<ImpactView, 'id' | 'bornAt'>) {
  scene.impacts.push({ ...view, id: ++impactSeq, bornAt: performance.now() / 1000 });
  // Bound the list: old flashes are culled by the renderer anyway.
  if (scene.impacts.length > 48) scene.impacts.splice(0, scene.impacts.length - 48);
}

export function kickShake(amount: number) {
  scene.shake = Math.min(1.4, scene.shake + amount);
}

/** Freeze-frame on a big beat, then a short slow-motion tail. */
export function triggerHitStop(freezeSeconds: number, slowSeconds: number, slowScale = 0.28) {
  scene.hitStop = Math.max(scene.hitStop, freezeSeconds);
  scene.timeScale = slowScale;
  window.setTimeout(() => {
    scene.timeScale = 1;
  }, (freezeSeconds + slowSeconds) * 1000);
}

export function resetScene(mode: SceneBus['mode']) {
  scene.mode = mode;
  // Back on the mark. The tutorial has no footwork at all, so without this a
  // duellist who walked in a match would open the tutorial standing off it.
  const home = stanceHome(scene.selfSide);
  scene.selfX = home.x;
  scene.selfZ = home.z;
  scene.serverSelfX = home.x;
  scene.serverSelfZ = home.z;
  scene.selfStunned = false;
  scene.selfSlowed = false;
  const opponentHome = stanceHome(scene.oppSide);
  scene.oppX = opponentHome.x;
  scene.oppZ = opponentHome.z;
  scene.projectiles = [];
  scene.impacts = [];
  scene.danger = 0;
  scene.hitStop = 0;
  scene.timeScale = 1;
  scene.shake = 0;
  scene.casting = false;
  scene.circle.active = false;
  scene.circle.progress = 0;
  scene.circle.burstAt = 0;
}
